// 세션 창 사이의 요청 전달부 — ① 메인 창 → **세션 창**(session.html) 새 탭, ② 세션 창끼리 **탭 이동**
// (끌어서 창 밖에 놓으면 새 창으로 분리, 다른 세션 창에 놓으면 그 창의 탭으로 합치기 — TeamViewer·Chrome 식).
//
// 전달 방식이 환경마다 다르다(여기 한 곳에 가둔다):
//   - 웹(브라우저): 주 세션 창은 이름 붙은 창 하나(`SESSION_WINDOW_NAME`)를 재사용 → 준비 표식(`__RS_SESSION_READY__`)이
//     서면 `postMessage`(같은 오리진만). 창이 이미 열려 있으면 **다시 불러오지 않는다**(열린 세션이 죽는다).
//     세션 창끼리는 `BroadcastChannel` 로 **자기 화면 위치**를 주기적으로 알린다 → 탭을 놓은 지점이 어느 창 위인지 판정.
//   - 통합앱(Tauri): Rust 가 세션 창을 만들고 요청을 **창별 대기열**에 쌓는다 → 세션 창이 꺼내 간다(창 생성 전·리스너
//     부착 전에 온 요청도 잃지 않는다). 놓은 지점 판정도 Rust 가 **OS 커서 위치**로 한다(웹뷰의 화면 좌표는 창 테두리·
//     배율 때문에 믿기 어렵다). 명령·이벤트 이름은 `src-tauri/src/lib.rs` 와 같아야 한다.
//
// ⚠️ 원격 연결(RTCPeerConnection)은 만든 창에 묶여 있어 다른 창으로 **옮길 수 없다** — 탭 이동 = 옮겨 간 창에서
//    같은 요청으로 **다시 연결**하고 원래 창의 연결은 끊는다(몇 초 재접속).
// ⚠️ 요청에는 로그인 토큰이 들어 있다(types.ts) — 같은 오리진 창·앱 내부로만 보내고 로그에 남기지 않는다.
import { SERVER } from '../config';
import { IS_SHELL } from '../shell';
import type { DropPoint, DropTarget, SessionRequest } from './types';

/** 웹: 주 세션 창 이름(같은 이름이면 브라우저가 기존 창을 돌려준다). 메인 창의 새 접속은 항상 이 창으로 간다. */
const SESSION_WINDOW_NAME = 'rs-sessions';
/** 웹: 분리한 세션 창 이름 접두사(뒤에 고유값) — 주 창과 이름이 달라 메인 창의 새 접속이 이 창으로 새지 않는다. */
const DETACHED_WINDOW_PREFIX = 'rs-sessions-';
/** 세션 창 페이지(빌드 엔트리 — vite.config.ts `session`). */
const SESSION_PAGE = 'session.html';
/** 웹: 창 간 메시지 종류(opener → 새 창). */
const MSG_OPEN = 'rs-open-session';
/** 웹: 세션 창이 요청을 받을 준비가 됐다는 표식(같은 오리진이라 창 객체 속성으로 직접 읽는다). */
const READY_FLAG = '__RS_SESSION_READY__';
/** 웹: 새 창이 준비될 때까지 기다리는 한도·주기(ms). */
const READY_WAIT_MS = 20_000;
const READY_POLL_MS = 100;

/** 웹: 세션 창끼리 위치 알림·합치기 요청을 주고받는 채널(같은 오리진 전용). */
const WINDOWS_CHANNEL = 'rs-session-windows';
/** 웹: 세션 창이 자기 화면 위치를 알리는 주기와, 알림이 끊긴 창을 목록에서 빼는 유효 시간(ms). */
const PRESENCE_INTERVAL_MS = 1_000;
const PRESENCE_TTL_MS = 3_500;
/** 웹: 합치기 요청의 수신 확인 대기(ms) — 대상 창이 방금 닫혔으면 탭을 잃지 않게 원래 창에 남긴다. */
const DOCK_ACK_MS = 1_500;
/** 분리한 새 창을 놓을 때 커서가 창의 어디를 잡은 것처럼 둘지(탭 줄 왼쪽 근처, 논리 px). 🔴 lib.rs `DETACH_GRAB_*` 와 같게. */
const DETACH_GRAB_X = 120;
const DETACH_GRAB_Y = 16;

/** Tauri 명령·이벤트 — 🔴 lib.rs 의 같은 이름 명령·`SESSION_QUEUE_EVENT` 와 같아야 한다. */
const TAURI_CMD_OPEN = 'open_session_window';
const TAURI_CMD_TAKE = 'take_session_requests';
const TAURI_CMD_CLOSE = 'close_session_window';
const TAURI_CMD_DROP = 'session_drop_target';
const TAURI_CMD_PREVIEW_BEGIN = 'drag_preview_begin';
const TAURI_CMD_PREVIEW_END = 'drag_preview_end';
const TAURI_CMD_TAB_COUNT = 'session_tab_count';
const TAURI_CMD_LIVE_TOTAL = 'session_live_total';
/** 세션 창들의 진행 중 세션 합계가 바뀌면 Rust 가 메인 창에 보내는 이벤트(내용 = 합계). 🔴 lib.rs `SESSION_TOTAL_EVENT`. */
const TAURI_SESSION_TOTAL_EVENT = 'rs-session-total';
/** 창(제목 표시줄) 끌기로 다른 세션 창 탭 줄에 놓았을 때 Rust 가 끌린 창에 보내는 이벤트(내용 = 대상 창 label). 🔴 lib.rs `WINDOW_DOCK_EVENT`. */
const TAURI_WINDOW_DOCK_EVENT = 'rs-window-dock';
const TAURI_QUEUE_EVENT = 'rs-session-queue';
/** 기존 세션 창이 요청을 꺼내 가지 않았을 때 Rust 가 돌려주는 오류 값. 🔴 lib.rs `SESSION_WINDOW_UNRESPONSIVE`. */
const SESSION_WINDOW_UNRESPONSIVE = 'session_window_unresponsive';

const NO_DROP: DropTarget = { kind: 'none' };

/**
 * 앱: 끌기 번호 — 미리보기 켜기·끄기를 짝짓는다. 🔴 Rust(`drag_preview_*`)는 이 번호로 **순서 뒤바뀜**을 가린다
 * (켜기는 async 라 끄기가 먼저 도착할 수 있다 → 끄기가 먼저 오면 늦게 온 켜기를 무시). 시각 기반이라 나중 끌기가 항상 더 크다
 * (창마다 모듈이 따로여도). 같은 ms 안 연속 끌기를 가르는 일련번호 폭 = `DRAG_ID_SEQ_SPAN`(Number 안전 정수 범위 안).
 */
const DRAG_ID_SEQ_SPAN = 1000;
let dragId = 0;
let dragSeq = 0;

// ── 웹: 새 창에 요청 보내기(준비될 때까지 대기) ─────────────────────────────

interface Outgoing {
  win: Window;
  req: SessionRequest;
  t0: number;
}
const outbox: Outgoing[] = [];
let pumpTimer: number | undefined;

/** 세션 창 URL — 명시 서버가 있으면 `?server=` 로 넘긴다(세션 창의 config 는 로드 시점에 서버를 정한다). */
function sessionPageUrl(): string {
  const u = new URL(SESSION_PAGE, location.href);
  if (SERVER) {
    u.searchParams.set('server', SERVER);
  }
  return u.toString();
}

function isReady(w: Window): boolean {
  try {
    return (w as unknown as Record<string, unknown>)[READY_FLAG] === true;
  } catch {
    return false; // 다른 오리진(있을 수 없지만) — 준비 안 됨으로 본다
  }
}

/** 준비된 창에 쌓인 요청을 순서대로 보낸다. 창이 닫혔거나 한도를 넘긴 요청은 버린다. */
function pump(): void {
  if (pumpTimer !== undefined) {
    return;
  }
  pumpTimer = window.setInterval(() => {
    const now = Date.now();
    const rest: Outgoing[] = [];
    for (const o of outbox) {
      if (o.win.closed || now - o.t0 > READY_WAIT_MS) {
        continue;
      }
      if (isReady(o.win)) {
        o.win.postMessage({ type: MSG_OPEN, request: o.req }, location.origin);
      } else {
        rest.push(o);
      }
    }
    outbox.splice(0, outbox.length, ...rest);
    if (!outbox.length) {
      window.clearInterval(pumpTimer);
      pumpTimer = undefined;
    }
  }, READY_POLL_MS);
}

function sendWhenReady(win: Window, req: SessionRequest): void {
  outbox.push({ win, req, t0: Date.now() });
  pump();
}

// ── 메인 창 쪽 ────────────────────────────────────────────────────────────

let sessionWin: Window | null = null;

/**
 * 세션 창으로 넘기지 못한 이유(통합앱만 — 결과가 늦게 온다).
 * - `unresponsive`: 세션 창이 있는데 요청을 꺼내 가지 않았다(웹 화면이 죽었거나 멈춤) → "다시 열기" 로 창을 새로 만든다.
 * - `error`: 그 밖의 실패(창 생성 실패 등) — `message` 를 그대로 보여 준다.
 */
export type SessionOpenFailure = { kind: 'unresponsive' } | { kind: 'error'; message: string };

/** 통합앱 명령 실패를 분류한다(Tauri 는 Rust `Err(String)` 을 문자열 그대로 reject 한다). */
function classifyOpenError(e: unknown): SessionOpenFailure {
  const message = typeof e === 'string' ? e : e instanceof Error ? e.message : String(e);
  return message === SESSION_WINDOW_UNRESPONSIVE ? { kind: 'unresponsive' } : { kind: 'error', message };
}

/**
 * 원격 세션을 세션 창의 새 탭으로 연다. **클릭 처리 안에서 동기적으로** 불러야 한다(브라우저 팝업 차단 규칙).
 * @param req 넘길 요청 — **약속**으로 받는다. 창은 지금(동기로) 열고, 요청은 준비되면 넘긴다
 *   (로그인 토큰을 넘기기 직전에 갱신하느라 기다려야 한다 — `Api.freshToken`).
 * @param onFailure 통합앱에서 넘기기가 **나중에** 실패했을 때 불린다(넘기려던 요청과 함께).
 *   🔴 종전에는 명령 결과를 보지 않아, 세션 창이 죽어 있으면 접속 버튼이 **아무 반응 없이** 끝났다(2026-09-17 결함).
 * @returns 넘겼으면 true, 팝업이 차단돼 못 열었으면 false(호출측은 같은 페이지 탭으로 대신 연다)
 */
export function openInSessionWindow(
  req: Promise<SessionRequest>,
  onFailure?: (f: SessionOpenFailure, req: SessionRequest) => void,
): boolean {
  if (IS_SHELL) {
    void Promise.all([import('@tauri-apps/api/core'), req]).then(([{ invoke }, r]) =>
      invoke(TAURI_CMD_OPEN, { request: r }).catch((e: unknown) => {
        const f = classifyOpenError(e);
        console.warn('[sessions] 세션 창으로 넘기지 못함:', f.kind === 'error' ? f.message : f.kind);
        onFailure?.(f, r);
      }),
    );
    return true;
  }
  // 이미 연 창이 살아 있으면 그대로, 아니면 이름으로 찾는다 — `window.open('', 이름)` 은 기존 창을 **다시 불러오지 않고**
  // 돌려준다(메인 창을 새로고침해 참조를 잃어도 열린 세션을 죽이지 않는다). 없으면 빈 창이 생긴다.
  const w = sessionWin && !sessionWin.closed ? sessionWin : window.open('', SESSION_WINDOW_NAME);
  if (!w) {
    return false;
  }
  sessionWin = w;
  let onPage = false;
  try {
    onPage = w.location.pathname.endsWith(`/${SESSION_PAGE}`);
  } catch {
    onPage = false;
  }
  if (!onPage) {
    w.location.href = sessionPageUrl();
  }
  void req.then((r) => sendWhenReady(w, r));
  w.focus();
  return true;
}

/**
 * 통합앱: 응답 없는 세션 창을 **없애고** 새 세션 창에서 연다(`unresponsive` 뒤 사용자가 "다시 열기" 를 눌렀을 때).
 * ⚠️ 그 창에 남은 탭(세션)은 모두 닫힌다 — 그래서 자동으로 하지 않고 사람이 누를 때만 한다.
 * @returns 실패하면 reject(분류된 `SessionOpenFailure`)
 */
export async function reopenInFreshSessionWindow(req: SessionRequest): Promise<void> {
  const { invoke } = await import('@tauri-apps/api/core');
  try {
    await invoke(TAURI_CMD_OPEN, { request: req, replace: true });
  } catch (e) {
    throw classifyOpenError(e);
  }
}

// ── 웹: 세션 창끼리 위치 알림(presence) ─────────────────────────────────────

/** 화면 좌표계의 사각형(논리 px). */
interface ScreenRect {
  x: number;
  y: number;
  w: number;
  h: number;
}
interface Peer {
  rect: ScreenRect;
  /** 마지막으로 포커스를 받은 시각 — 창이 겹친 곳에 놓으면 가장 최근 창(= 위에 있을 가능성이 큰 창)으로 간다. */
  focusedAt: number;
  seenAt: number;
}
type ChannelMsg =
  | { type: 'presence'; name: string; rect: ScreenRect; focusedAt: number }
  | { type: 'bye'; name: string }
  | { type: 'dock'; id: string; to: string; request: SessionRequest }
  | { type: 'dock-ack'; id: string };

const peers = new Map<string, Peer>();
let channel: BroadcastChannel | null = null;
const dockAcks = new Map<string, () => void>();

/**
 * 창 내용 영역의 화면 위치 보정값(`window.screenX` 기준). `screenX` 는 **창 테두리 바깥** 기준이라 제목줄·테두리만큼
 * 어긋난다 → 포인터 이벤트가 올 때마다 `screenX - clientX` 로 실측해 둔다. 실측 전에는 outer/inner 차이로 어림한다.
 */
let originDX: number | null = null;
let originDY: number | null = null;

function calibrate(e: PointerEvent): void {
  originDX = e.screenX - e.clientX - window.screenX;
  originDY = e.screenY - e.clientY - window.screenY;
}

function contentRect(): ScreenRect {
  const dx = originDX ?? Math.max(0, (window.outerWidth - window.innerWidth) / 2);
  const dy = originDY ?? Math.max(0, window.outerHeight - window.innerHeight - dx);
  return { x: window.screenX + dx, y: window.screenY + dy, w: window.innerWidth, h: window.innerHeight };
}

function contains(r: ScreenRect, x: number, y: number): boolean {
  return x >= r.x && y >= r.y && x < r.x + r.w && y < r.y + r.h;
}

/** 이름 없는 세션 창(주소를 직접 연 경우 등)에도 고유 이름을 준다 — 합치기 요청의 주소가 창 이름이다. */
function ensureWindowName(): void {
  if (!window.name.startsWith(SESSION_WINDOW_NAME)) {
    window.name = DETACHED_WINDOW_PREFIX + Math.random().toString(36).slice(2, 10);
  }
}

/** 웹 세션 창: 위치 알림 시작 + 다른 창이 보낸 합치기 요청 수신. @returns 정리 함수 */
function startPresence(onDock: (req: SessionRequest) => void): () => void {
  ensureWindowName();
  const me = window.name;
  const ch = new BroadcastChannel(WINDOWS_CHANNEL);
  let focusedAt = document.hasFocus() ? Date.now() : 0;
  const announce = () => ch.postMessage({ type: 'presence', name: me, rect: contentRect(), focusedAt } satisfies ChannelMsg);
  ch.onmessage = (e: MessageEvent<ChannelMsg>) => {
    const m = e.data;
    if (m.type === 'presence' && m.name !== me) {
      peers.set(m.name, { rect: m.rect, focusedAt: m.focusedAt, seenAt: Date.now() });
    } else if (m.type === 'bye') {
      peers.delete(m.name);
    } else if (m.type === 'dock' && m.to === me) {
      ch.postMessage({ type: 'dock-ack', id: m.id } satisfies ChannelMsg);
      window.focus();
      onDock(m.request);
    } else if (m.type === 'dock-ack') {
      dockAcks.get(m.id)?.();
    }
  };
  const onFocus = () => {
    focusedAt = Date.now();
    announce();
  };
  window.addEventListener('focus', onFocus);
  window.addEventListener('resize', announce);
  window.addEventListener('pointermove', calibrate, { passive: true });
  window.addEventListener('pointerdown', calibrate, { passive: true });
  const timer = window.setInterval(announce, PRESENCE_INTERVAL_MS);
  announce();
  channel = ch;
  return () => {
    window.clearInterval(timer);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('resize', announce);
    window.removeEventListener('pointermove', calibrate);
    window.removeEventListener('pointerdown', calibrate);
    ch.postMessage({ type: 'bye', name: me } satisfies ChannelMsg);
    ch.close();
    channel = null;
    peers.clear();
  };
}

// ── 세션 창 쪽 ────────────────────────────────────────────────────────────

/**
 * 메인 창·다른 세션 창이 보내는 세션 요청을 구독한다. @returns 구독 해제 함수
 *
 * Tauri: 이 창의 대기열을 **꺼내 가는**(take) 방식이라 같은 요청이 두 번 오지 않는다. 꺼낸 결과는 구독이 이미 풀렸어도
 * 처리한다(개발 모드 StrictMode 이중 마운트에서 첫 구독이 꺼내 간 요청을 버리면 영영 사라진다 — 중복은 탭 관리자가 막는다).
 * 알림 이벤트는 모든 세션 창이 받지만 대기열은 창별이라, 요청은 **보낸 대상 창**에서만 나온다.
 */
export function subscribeSessionRequests(onRequest: (req: SessionRequest) => void): () => void {
  if (IS_SHELL) {
    let unlisten: (() => void) | undefined;
    let alive = true;
    void (async () => {
      const { invoke } = await import('@tauri-apps/api/core');
      const { listen } = await import('@tauri-apps/api/event');
      const drain = () =>
        invoke<SessionRequest[]>(TAURI_CMD_TAKE)
          .then((list) => list.forEach(onRequest))
          .catch(() => {});
      const un = await listen(TAURI_QUEUE_EVENT, () => void drain());
      if (alive) {
        unlisten = un;
      } else {
        un();
      }
      void drain();
    })();
    return () => {
      alive = false;
      unlisten?.();
    };
  }
  const onMessage = (e: MessageEvent) => {
    if (e.origin !== location.origin || e.data?.type !== MSG_OPEN || !e.data.request) {
      return;
    }
    onRequest(e.data.request as SessionRequest);
  };
  window.addEventListener('message', onMessage);
  const stopPresence = startPresence(onRequest);
  (window as unknown as Record<string, unknown>)[READY_FLAG] = true;
  return () => {
    window.removeEventListener('message', onMessage);
    stopPresence();
    (window as unknown as Record<string, unknown>)[READY_FLAG] = false;
  };
}

/** 세션 창을 닫는다(마지막 탭을 닫았을 때). 웹은 스크립트로 연 창이라 `window.close()` 가 허용된다. */
export function closeSessionWindow(): void {
  if (IS_SHELL) {
    void import('@tauri-apps/api/core').then(({ invoke }) => invoke(TAURI_CMD_CLOSE));
    return;
  }
  window.close();
}

// ── 탭 끌어 놓기(분리·합치기) ─────────────────────────────────────────────

/**
 * 탭을 놓은 지점이 어디인지 판정한다.
 * @param pt 포인터를 뗀 지점
 * @param allowDetach 새 창으로 분리해도 되는지(탭이 1개면 분리 = 창 이동일 뿐이라 막는다 — 합치기는 된다)
 */
export async function resolveTabDrop(pt: DropPoint, allowDetach: boolean): Promise<DropTarget> {
  if (IS_SHELL) {
    const { invoke } = await import('@tauri-apps/api/core');
    return invoke<DropTarget>(TAURI_CMD_DROP, { allowDetach }).catch(() => NO_DROP);
  }
  const inside = pt.clientX >= 0 && pt.clientY >= 0 && pt.clientX < window.innerWidth && pt.clientY < window.innerHeight;
  if (inside) {
    return NO_DROP; // 자기 창 안 — 탭 줄이었으면 호출측(SessionTabs)이 순서 바꾸기로 처리한다
  }
  const now = Date.now();
  let best: { name: string; peer: Peer } | undefined;
  for (const [name, peer] of peers) {
    if (now - peer.seenAt > PRESENCE_TTL_MS || !contains(peer.rect, pt.screenX, pt.screenY)) {
      continue;
    }
    if (!best || peer.focusedAt > best.peer.focusedAt) {
      best = { name, peer };
    }
  }
  if (best) {
    return { kind: 'dock', window: best.name };
  }
  if (!allowDetach) {
    return NO_DROP;
  }
  return {
    kind: 'detach',
    x: pt.screenX - DETACH_GRAB_X,
    y: pt.screenY - DETACH_GRAB_Y,
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

/**
 * 끄는 동안 **창 밖 미리보기**(창 크기의 희미한 사각형 + 안내)를 켠다 — 앱 전용. Rust 가 커서를 따라 옮기고,
 * 놓으면 새 창이 생길 바로 그 자리를 보여 준다(다른 세션 창 위면 "합치기" 안내, 원래 창 안이면 숨김).
 * 브라우저는 창 밖에 그릴 수 없어 아무것도 하지 않는다(창 안 그림자만).
 * @param label 탭 이름(안내 문구에 쓴다) · @param allowDetach 분리 가능 여부(탭 1개면 "분리할 수 없음" 안내)
 */
export function beginDragPreview(label: string, allowDetach: boolean): void {
  if (!IS_SHELL) {
    return;
  }
  dragSeq = (dragSeq + 1) % DRAG_ID_SEQ_SPAN;
  dragId = Date.now() * DRAG_ID_SEQ_SPAN + dragSeq;
  const id = dragId;
  void import('@tauri-apps/api/core')
    .then(({ invoke }) => invoke(TAURI_CMD_PREVIEW_BEGIN, { label, allowDetach, dragId: id }))
    .catch(() => {});
}

/** 창 밖 미리보기를 끈다(놓기·취소 모두). 켤 때의 번호로 짝을 맞춘다. */
export function endDragPreview(): void {
  if (!IS_SHELL || !dragId) {
    return;
  }
  const id = dragId;
  dragId = 0;
  void import('@tauri-apps/api/core')
    .then(({ invoke }) => invoke(TAURI_CMD_PREVIEW_END, { dragId: id }))
    .catch(() => {});
}

/**
 * 앱: 이 세션 창의 탭 현황을 알린다.
 * @param count 탭 수 — 창(제목 표시줄) 끌기 합치기는 **탭 1개 창만**이라 Rust 가 판정에 쓴다
 * @param live 진행 중(접속 중·연결됨) 세션 수 — Rust 가 모든 세션 창 합계를 메인 창에 알린다(업데이트 배너)
 */
export function reportTabCount(count: number, live: number): void {
  if (!IS_SHELL) {
    return;
  }
  void import('@tauri-apps/api/core')
    .then(({ invoke }) => invoke(TAURI_CMD_TAB_COUNT, { count, live }))
    .catch(() => {});
}

/**
 * 앱(메인 창): 세션 창들의 진행 중 세션 합계를 구독한다 — 현재 값을 먼저 한 번 받고, 이후 바뀔 때마다 받는다.
 * 🔴 업데이트 배너가 "원격 세션 중이면 설치 버튼 숨김" 에 쓴다(설치 = 앱 재시작 = 세션 창의 세션까지 전부 끊김).
 *    브라우저는 업데이트가 없어 아무것도 하지 않는다(항상 0 으로 둔다).
 * @returns 구독 해제 함수
 */
export function subscribeLiveSessionTotal(onTotal: (total: number) => void): () => void {
  if (!IS_SHELL) {
    return () => {};
  }
  let unlisten: (() => void) | undefined;
  let alive = true;
  void (async () => {
    const { invoke } = await import('@tauri-apps/api/core');
    const { listen } = await import('@tauri-apps/api/event');
    const un = await listen<number>(TAURI_SESSION_TOTAL_EVENT, (e) => alive && onTotal(e.payload));
    if (alive) {
      unlisten = un;
    } else {
      un();
      return;
    }
    // 리스너를 붙인 **뒤에** 현재 값을 묻는다(그 사이 바뀐 값을 놓치지 않게 — 늦게 온 조회 결과가 더 최신일 수는 없다:
    // 조회는 Rust 표의 지금 값이라 이후 이벤트와 같거나 앞선 값이고, 이후 변경은 이벤트로 다시 온다).
    const total = await invoke<number>(TAURI_CMD_LIVE_TOTAL).catch(() => 0);
    if (alive) {
      onTotal(total);
    }
  })();
  return () => {
    alive = false;
    unlisten?.();
  };
}

/**
 * 앱: 이 창을 제목 표시줄로 끌어 다른 세션 창 탭 줄에 놓았다는 알림을 구독한다(Windows 앱 전용 — Rust 가 판정).
 * @param onDock 대상 창 label 을 받는다 — 호출측은 탭 합치기와 같은 경로로 탭을 옮긴다.
 * @returns 구독 해제 함수
 */
export function subscribeWindowDock(onDock: (target: string) => void): () => void {
  if (!IS_SHELL) {
    return () => {};
  }
  let unlisten: (() => void) | undefined;
  let alive = true;
  void (async () => {
    const { getCurrentWebviewWindow } = await import('@tauri-apps/api/webviewWindow');
    const un = await getCurrentWebviewWindow().listen<string>(TAURI_WINDOW_DOCK_EVENT, (e) => onDock(e.payload));
    if (alive) {
      unlisten = un;
    } else {
      un();
    }
  })();
  return () => {
    alive = false;
    unlisten?.();
  };
}

/** 합치기 요청을 보내고 대상 창의 수신 확인을 기다린다. */
function sendDock(to: string, req: SessionRequest): Promise<boolean> {
  const ch = channel;
  if (!ch) {
    return Promise.resolve(false);
  }
  const id = Math.random().toString(36).slice(2);
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      dockAcks.delete(id);
      resolve(false);
    }, DOCK_ACK_MS);
    dockAcks.set(id, () => {
      window.clearTimeout(timer);
      dockAcks.delete(id);
      resolve(true);
    });
    ch.postMessage({ type: 'dock', id, to, request: req } satisfies ChannelMsg);
  });
}

/**
 * 판정한 곳으로 요청을 넘긴다(합치기 = 그 창의 새 탭, 분리 = 그 자리에 새 세션 창).
 * ⚠️ 분리(웹)는 `window.open` 이라 **포인터를 뗀 직후**(사용자 활성화가 살아 있을 때) 불러야 팝업 차단을 피한다.
 * @returns 넘겼으면 true — 호출측은 그때만 원래 탭을 닫는다(실패하면 탭을 잃지 않게 그대로 둔다)
 */
export async function deliverSessionRequest(req: SessionRequest, target: DropTarget): Promise<boolean> {
  if (target.kind === 'none') {
    return false;
  }
  if (IS_SHELL) {
    const { invoke } = await import('@tauri-apps/api/core');
    return invoke(TAURI_CMD_OPEN, { request: req, target })
      .then(() => true)
      .catch(() => false);
  }
  if (target.kind === 'dock') {
    return sendDock(target.window, req);
  }
  const name = DETACHED_WINDOW_PREFIX + Math.random().toString(36).slice(2, 10);
  const features = [
    'popup',
    `left=${Math.round(target.x)}`,
    `top=${Math.round(target.y)}`,
    `width=${Math.round(target.width)}`,
    `height=${Math.round(target.height)}`,
  ].join(',');
  const w = window.open(sessionPageUrl(), name, features);
  if (!w) {
    return false;
  }
  sendWhenReady(w, req);
  return true;
}
